import type { FacilityId, ResourceWallet, SaveData } from './types';

export const SAVE_KEY = 'breakline-save-v1';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function createDefaultSave(): SaveData {
  return {
    saveVersion: 1,
    resources: { food: 160, metal: 140, electronics: 90, blueprints: 1 },
    facilities: { hq: 1, barracks: 1, workshop: 1, range: 1, comms: 1 },
    captainId: 'vanguard',
    unlockedStage: 1,
    stageStars: {},
    tutorialSeen: false,
    settings: { sound: true, reducedMotion: false },
    totalRuns: 0,
  };
}

function isResourceWallet(value: unknown): value is ResourceWallet {
  if (!value || typeof value !== 'object') return false;
  const wallet = value as Record<string, unknown>;
  return ['food', 'metal', 'electronics', 'blueprints'].every(
    (key) => typeof wallet[key] === 'number' && Number.isFinite(wallet[key]),
  );
}

export function validateSave(value: unknown): value is SaveData {
  if (!value || typeof value !== 'object') return false;
  const data = value as Partial<SaveData>;
  if (data.saveVersion !== 1 || !isResourceWallet(data.resources)) return false;
  if (!data.facilities || !data.settings || !data.stageStars) return false;
  const facilityIds: FacilityId[] = ['hq', 'barracks', 'workshop', 'range', 'comms'];
  if (!facilityIds.every((id) => Number.isInteger(data.facilities?.[id]) && (data.facilities?.[id] ?? 0) >= 1)) return false;
  if (!['vanguard', 'gunner', 'technician'].includes(data.captainId ?? '')) return false;
  if (!Number.isInteger(data.unlockedStage) || (data.unlockedStage ?? 0) < 1) return false;
  if (typeof data.tutorialSeen !== 'boolean' || typeof data.totalRuns !== 'number') return false;
  if (typeof data.settings.sound !== 'boolean' || typeof data.settings.reducedMotion !== 'boolean') return false;
  return true;
}

export function loadSave(storage: StorageLike = window.localStorage): SaveData {
  const raw = storage.getItem(SAVE_KEY);
  if (!raw) return createDefaultSave();
  try {
    const parsed: unknown = JSON.parse(raw);
    return validateSave(parsed) ? parsed : createDefaultSave();
  } catch {
    return createDefaultSave();
  }
}

export function persistSave(save: SaveData, storage: StorageLike = window.localStorage): void {
  storage.setItem(SAVE_KEY, JSON.stringify(save));
}

export function exportSave(save: SaveData): string {
  return JSON.stringify(save, null, 2);
}

export function importSave(raw: string): SaveData {
  const parsed: unknown = JSON.parse(raw);
  if (!validateSave(parsed)) throw new Error('セーブデータの形式が正しくありません。');
  return parsed;
}

export function addResources(save: SaveData, rewards: ResourceWallet): SaveData {
  return {
    ...save,
    resources: {
      food: save.resources.food + rewards.food,
      metal: save.resources.metal + rewards.metal,
      electronics: save.resources.electronics + rewards.electronics,
      blueprints: save.resources.blueprints + rewards.blueprints,
    },
  };
}
